import React from 'react'
import { CloseButton, Divider } from '@mantine/core'
import { useSwipeable } from 'react-swipeable'
import Link from 'next/link'

import PictureUploadButton from './PictureUploadButton'
import BrandLoader from 'components/BrandLoader'
import * as Types from '../../../declarations'
import { getUploadThumbPath } from '../../../lib/display'
import { sluggify } from '../../../lib/helper'

interface ImageModalProps {
	activeImageIndex: number
	changeActiveImageIndex: (index: number) => void
	images: Types.DB.Models.UploadAttributes[]
}

type LightboxImage = Pick<
	Types.DB.Models.UploadAttributes,
	'id' | 'uploadedBy' | 'attributionText' | 'attributionLink' | 'source'
> & {
	largeSource: string
	user?: { guideCompany?: { id: number; companyName: string } | null } | null
}

const imageIndexOfDelta = (
	delta: number,
	images: LightboxImage[],
	activeImageIndex: number,
): number => {
	const newIndex = (images.length + activeImageIndex + delta) % images.length

	return newIndex
}

const ImageModal: React.FC<ImageModalProps> = ({
	activeImageIndex,
	changeActiveImageIndex,
	images,
}: ImageModalProps) => {
	const [loadedSource, setLoadedSource] = React.useState<string | null>(null)

	const lightboxImages: LightboxImage[] = React.useMemo(() => {
		return images.map(
			({ id, uploadedBy, attributionText, attributionLink, source, user }) => ({
				id,
				uploadedBy,
				attributionText,
				attributionLink,
				source,
				largeSource: getUploadThumbPath(id, 'large'),
				user,
			}),
		)
	}, [images])

	const isOpen =
		activeImageIndex !== -1 &&
		lightboxImages.length > 0 &&
		activeImageIndex < lightboxImages.length

	const preloadImage = (index: number) => {
		const srcToPreload = lightboxImages[index].largeSource

		const img = new Image()
		img.src = srcToPreload
	}

	const preloadNeighbouringImagesToIndex = React.useCallback(
		(index: number) => {
			preloadImage(imageIndexOfDelta(1, lightboxImages, index))
			preloadImage(imageIndexOfDelta(2, lightboxImages, index))
			preloadImage(imageIndexOfDelta(-1, lightboxImages, index))
			preloadImage(imageIndexOfDelta(-2, lightboxImages, index))
		},
		[activeImageIndex],
	)

	const changeIndex = React.useCallback(
		(delta: number) => {
			if (lightboxImages.length === 0) return
			const newIndex = imageIndexOfDelta(
				delta,
				lightboxImages,
				activeImageIndex,
			)
			changeActiveImageIndex(newIndex)
		},
		[activeImageIndex, lightboxImages],
	)

	const left = React.useCallback(() => {
		changeIndex(-1)
	}, [changeIndex])

	const right = React.useCallback(() => {
		changeIndex(1)
	}, [changeIndex])

	const handleKeyDown = React.useCallback(
		(event: KeyboardEvent) => {
			if (activeImageIndex === -1) return
			switch (event.key) {
				case 'Escape':
					changeActiveImageIndex(-1)
					break
				case 'ArrowLeft':
					left()
					break
				case 'ArrowRight':
					right()
					break
				default:
					break
			}
		},
		[activeImageIndex, left, right, changeActiveImageIndex],
	)

	const swipeHandlers = useSwipeable({
			onSwipedLeft: () => right(),
			onSwipedRight: () => left(),
			// preventDefaultTouchmoveEvent: true,
			trackMouse: true,
	})

	React.useEffect(() => {
		window.addEventListener('keydown', handleKeyDown)

		// Cleanup function to remove the event listener when the component is unmounted or before it's re-rendered
		return () => window.removeEventListener('keydown', handleKeyDown)
	}, [handleKeyDown])

	if (!isOpen) return null

	const currentSource = lightboxImages[activeImageIndex].largeSource

	return (
		<div id="images-lightbox" className="modal">
			<CloseButton
				title="Close popover"
				size="xl"
				iconSize={32}
				className="closeButton"
				onClick={() => changeActiveImageIndex(-1)}
			/>

			<div className="column left">
				<button className="arrowButton" onClick={() => left()}>
					←
				</button>
			</div>

			<div className="column middle" {...swipeHandlers}>
				<div className="image-container">
					<img
						src={currentSource}
						onLoad={() => {
							setLoadedSource(currentSource)
							// only warm neighbours once the visible image is in — otherwise
							// preloads compete with it for bandwidth
							preloadNeighbouringImagesToIndex(activeImageIndex)
						}}
						style={
							loadedSource === currentSource ? undefined : { display: 'none' }
						}
					/>
					{loadedSource !== currentSource && (
						<div className="lightbox-loading">
							<BrandLoader size={96} />
						</div>
					)}
					<div className="underneath-image">
						{lightboxImages[activeImageIndex].attributionLink === '' &&
							lightboxImages[activeImageIndex].attributionText === '' && (
								<>
									Image uploaded by
									{lightboxImages[activeImageIndex]?.user?.guideCompany
										?.companyName ? (
										<>
											&nbsp;
											<Link
												href={`/guides/${sluggify(
													lightboxImages[activeImageIndex]?.user?.guideCompany
														?.companyName as string,
												)}`}
											>
												{
													lightboxImages[activeImageIndex]?.user?.guideCompany
														?.companyName
												}
											</Link>
										</>
									) : (
										' a user'
									)}
								</>
							)}

						{lightboxImages[activeImageIndex].attributionLink !== '' &&
							lightboxImages[activeImageIndex].attributionText !== '' && (
								<>
									<a
										href={lightboxImages[activeImageIndex].attributionLink}
										target="_blank"
										rel="noopener noreferrer"
									>
										{lightboxImages[activeImageIndex].attributionText}
									</a>
								</>
							)}
						{lightboxImages[activeImageIndex].attributionLink === '' &&
							lightboxImages[activeImageIndex].attributionText !== '' && (
								<>{lightboxImages[activeImageIndex].attributionText}</>
							)}
						<Divider variant="dotted" />
						<div className="right">
							<PictureUploadButton />
						</div>
					</div>
				</div>
			</div>

			<div className="column right">
				<button className="arrowButton" onClick={() => right()}>
					→
				</button>
			</div>
		</div>
	)
}

export default ImageModal
